import type { ReactNode } from 'react';
import { Box, Button } from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';

const SURFACE_LOW = '#131313';
const SURFACE = '#1a1919';
const SURFACE_HI = '#262626';
const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_PRIMARY = '#392700';
const ON_VARIANT = '#adaaaa';

export interface WorkTabCounts {
    jobs: number;
    requests: number;
    invitations: number;
    completed: number;
}

export interface WorkManagementSectionProps {
    activeTab: string;
    tabOptions: { label: string; value: string }[];
    counts: WorkTabCounts;
    onTabChange: (value: string) => void;
    children: ReactNode;
}

const badgeBg = (value: string, active: boolean) => {
    if (active) return { bgcolor: '#000', color: GOLD };
    if (value === 'invitations') return { bgcolor: 'rgba(0,253,193,0.2)', color: MINT };
    return { bgcolor: SURFACE_HI, color: '#fff' };
};

export function WorkManagementSection({
    activeTab,
    tabOptions,
    counts,
    onTabChange,
    children,
}: WorkManagementSectionProps) {
    const badgeFor = (value: string) => {
        if (value === 'jobs') return counts.jobs;
        if (value === 'requests') return counts.requests;
        if (value === 'invitations') return counts.invitations;
        if (value === 'completed') return counts.completed;
        return 0;
    };

    return (
        <Box sx={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
                component="nav"
                aria-label="Work management sections"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    overflowX: 'auto',
                    p: 0.75,
                    borderRadius: 1.5,
                    bgcolor: SURFACE_LOW,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}>
                {tabOptions.map(opt => {
                    const active = activeTab === opt.value;
                    const n = badgeFor(opt.value);
                    const badge = badgeBg(opt.value, active);
                    return (
                        <Button
                            key={opt.value}
                            onClick={() => onTabChange(opt.value)}
                            disableRipple
                            sx={{
                                flex: '0 0 auto',
                                px: 2,
                                py: 1,
                                borderRadius: 1,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 12,
                                gap: 1,
                                color: active ? ON_PRIMARY : ON_VARIANT,
                                bgcolor: active ? GOLD : 'transparent',
                                '&:hover': {
                                    bgcolor: active ? GOLD : SURFACE,
                                    color: active ? ON_PRIMARY : '#fff',
                                },
                            }}>
                            {opt.label}
                            <Box
                                component="span"
                                sx={{
                                    px: 0.75,
                                    py: 0.1,
                                    borderRadius: 999,
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    lineHeight: 1.4,
                                    ...badge,
                                }}>
                                {n > 99 ? '99+' : n}
                            </Box>
                        </Button>
                    );
                })}
            </Box>
            <Box sx={{ width: '100%', minWidth: 0 }}>{children}</Box>
        </Box>
    );
}
